import envConfig from "../../../envConfig";

/**
 * Parse SSE data line and return the parsed JSON object
 * @param {string} line - The SSE data line (e.g., "data: {...}")
 * @returns {object|null} - Parsed JSON object or null if parsing fails
 */
const parseSSELine = (line) => {
  if (!line || !line.startsWith("data: ")) return null;
  try {
    const jsonStr = line.slice(6); // Remove "data: " prefix
    return JSON.parse(jsonStr);
  } catch (e) {
    console.warn("Failed to parse SSE line:", line, e);
    return null;
  }
};

/**
 * Quota error types for classification
 */
const QUOTA_ERROR_TYPES = {
  MONTHLY_LIMIT: "MONTHLY_LIMIT",
  RATE_LIMIT: "RATE_LIMIT",
  OTHER: "OTHER",
};

/**
 * Check if an error is a quota error (429)
 * @param {object} error - Error object with errorCode
 * @returns {boolean}
 */
const isQuotaError = (error) => {
  return error?.errorCode === 429;
};

/**
 * Classify the type of quota error based on the error message
 * @param {object} error - Error object with errorCode and errorMessage
 * @returns {string} - One of QUOTA_ERROR_TYPES
 */
const classifyQuotaError = (error) => {
  if (error?.errorCode !== 429) return QUOTA_ERROR_TYPES.OTHER;

  if (error?.errorMessage?.includes("monthly AI usage limit")) {
    return QUOTA_ERROR_TYPES.MONTHLY_LIMIT;
  }
  if (error?.errorMessage?.includes("Too many requests")) {
    return QUOTA_ERROR_TYPES.RATE_LIMIT;
  }
  return QUOTA_ERROR_TYPES.OTHER;
};

/**
 * Create a quota error object with classification
 * @param {object} errorData - Raw error data from API
 * @returns {Error} - Error with quota metadata
 */
const createQuotaError = (errorData) => {
  const error = new Error(errorData?.errorMessage || "Quota limit exceeded");
  error.isQuotaError = true;
  error.errorCode = errorData?.errorCode;
  error.errorMessage = errorData?.errorMessage;
  error.errorDetail = errorData?.errorDetail;
  error.quotaType = classifyQuotaError(errorData);
  return error;
};

// Timeout configuration
const INACTIVITY_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes
const FETCH_TIMEOUT_MS = 65 * 60 * 1000; // 65 minutes total request timeout (slightly longer than max agent runtime)

/**
 * Stream agent response using Server-Sent Events (SSE)
 * @param {object} params - The streaming parameters
 * @param {string} params.chatid - Chat ID (optional, will be created if not provided)
 * @param {string} params.workspaceid - Workspace ID
 * @param {string} params.token - API token
 * @param {Array} params.messages - Array of message objects with text and role
 * @param {string} params.agentid - Agent ID
 * @param {string} [params.baseImage] - Base image for vision models
 * @param {string} [params.canvasSelection] - Canvas selection data
 * @param {string} [params.methodName] - Endpoint method name ('agent' or 'planner'), defaults to 'agent'
 * @param {string} [params.nextMessageId] - Next message ID for planner mode
 * @param {string} [params.boundFolderPath] - Bound folder path for local folder access tool
 * @param {AbortSignal} params.signal - AbortController signal for cancellation
 * @param {function} params.onToken - Callback for text tokens: (chunk: string, messageId: string) => void
 * @param {function} params.onMessage - Callback for status/metadata updates: (data: object) => void
 * @param {function} params.onError - Callback for errors: (error: Error) => void
 * @param {function} params.onComplete - Callback for stream completion: () => void
 */
const streamAgentResponse = async ({
  chatid,
  workspaceid,
  token,
  messages,
  agentid,
  baseImage,
  canvasSelection,
  methodName = "agent",
  nextMessageId,
  boundFolderPath,
  signal,
  onToken,
  onMessage,
  onError,
  onComplete,
}) => {
  const url = `${envConfig.REACT_APP_AIS_ENDPOINT}/${methodName}`;

  // Timeout management
  let inactivityTimeoutId = null;
  let fetchTimeoutId = null;
  let reader = null;

  const clearAllTimeouts = () => {
    if (inactivityTimeoutId) {
      clearTimeout(inactivityTimeoutId);
      inactivityTimeoutId = null;
    }
    if (fetchTimeoutId) {
      clearTimeout(fetchTimeoutId);
      fetchTimeoutId = null;
    }
  };

  const resetInactivityTimeout = () => {
    if (inactivityTimeoutId) {
      clearTimeout(inactivityTimeoutId);
    }
    inactivityTimeoutId = setTimeout(() => {
      console.warn("SSE stream timed out due to 5 minutes of inactivity");
      if (reader) {
        reader.cancel("Inactivity timeout");
      }
      clearAllTimeouts();
      onError?.(new Error("Stream timed out due to inactivity"));
    }, INACTIVITY_TIMEOUT_MS);
  };

  // Create a combined signal that respects both user abort and fetch timeout
  const timeoutController = new AbortController();
  const combinedSignal = signal
    ? AbortSignal.any
      ? AbortSignal.any([signal, timeoutController.signal])
      : signal // Fallback for browsers without AbortSignal.any
    : timeoutController.signal;
  
  // Set fetch timeout (overall request timeout)
  fetchTimeoutId = setTimeout(() => {
    console.warn(`SSE stream timed out after ${FETCH_TIMEOUT_MS / 1000} seconds total`);
    timeoutController.abort();
    if (reader) {
      reader.cancel("Request timeout");
    }
    onError?.(new Error("Request timed out"));
  }, FETCH_TIMEOUT_MS);

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-api-key": token,
        "x-client-source": "toothfairyai",
      },
      body: JSON.stringify({
        workspaceid,
        chatid,
        messages,
        agentid,
        base_image: baseImage,
        canvas_selection: canvasSelection,
        raw_stream: true,
        stream: "true",
        ...(nextMessageId && { nextMessageId }),
        ...(boundFolderPath && { bound_folder_path: boundFolderPath }),
      }),
      signal: combinedSignal,
    });

    if (!response.ok) {
      // Handle quota/rate limit errors (429)
      if (response.status === 429) {
        try {
          const errorBody = await response.json();
          const errorData = errorBody?.body || errorBody;
          throw createQuotaError(errorData);
        } catch (parseError) {
          // If we can't parse the body, still throw a quota error
          if (parseError.isQuotaError) throw parseError;
          throw createQuotaError({
            errorCode: 429,
            errorMessage: "Rate limit exceeded. Please try again later.",
            errorDetail: "Usage limit exceeded",
          });
        }
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let currentMessageId = null;

    // Start the inactivity timeout
    resetInactivityTimeout();

    while (true) {
      const { done, value } = await reader.read();

      // Reset timeout on any data received
      if (value) {
        resetInactivityTimeout();
      }

      if (done) {
        clearAllTimeouts();
        // Process any remaining data in buffer
        if (buffer.trim()) {
          const lines = buffer.split("\n");
          for (const line of lines) {
            const trimmedLine = line.trim();
            if (trimmedLine) {
              const parsed = parseSSELine(trimmedLine);
              if (parsed) {
                const streamError = processSSEEvent(
                  parsed,
                  currentMessageId,
                  onToken,
                  onMessage
                );
                if (streamError) {
                  onError?.(streamError);
                  return;
                }
                if (parsed.message_id) currentMessageId = parsed.message_id;
                // Handle message_sealed: update to continuation message
                if (parsed.type === "message_sealed" && parsed.continuation_message_id) {
                  currentMessageId = parsed.continuation_message_id;
                }
              }
            }
          }
        }
        onComplete?.();
        break;
      }

      buffer += decoder.decode(value, { stream: true });

      // Process complete lines
      const lines = buffer.split("\n");
      // Keep the last incomplete line in the buffer
      buffer = lines.pop() || "";

      for (const line of lines) {
        const trimmedLine = line.trim();
        if (!trimmedLine) continue;

        const parsed = parseSSELine(trimmedLine);
        if (parsed) {
          const streamError = processSSEEvent(
            parsed,
            currentMessageId,
            onToken,
            onMessage
          );
          if (streamError) {
            clearAllTimeouts();
            if (reader) {
              reader.cancel("Stream error");
            }
            onError?.(streamError);
            return;
          }
          if (parsed.message_id) currentMessageId = parsed.message_id;
          // Handle message_sealed: update to continuation message
          if (parsed.type === "message_sealed" && parsed.continuation_message_id) {
            currentMessageId = parsed.continuation_message_id;
          }
        }
      }
    }
  } catch (error) {
    clearAllTimeouts();
    if (error.name === "AbortError") {
      // Distinguish between user abort and timeout abort
      const isTimeout = timeoutController.signal.aborted && !signal?.aborted;
      if (isTimeout) {
        console.warn("Stream timed out");
        onError?.(new Error("Request timed out"));
      } else {
        console.log("Stream aborted by user");
      }
    } else if (error.name === "TypeError" && error.message?.includes("network")) {
      // Network error mid-stream (proxy dropped connection, WiFi loss, etc.)
      console.error("Network error during stream:", error);
      const networkError = new Error("Network connection lost during streaming");
      networkError.isNetworkError = true;
      onError?.(networkError);
    } else {
      console.error("Stream error:", error);
      onError?.(error);
    }
  }
};

/**
 * Process a single SSE event and call appropriate callbacks
 * @returns {Error|null} - Returns an error if a quota error is detected, null otherwise
 */
const processSSEEvent = (data, currentMessageId, onToken, onMessage) => {
  try {
    return _processSSEEventInner(data, currentMessageId, onToken, onMessage);
  } catch (callbackError) {
    // Prevent callback errors from crashing the stream reader loop
    console.error("Error in SSE callback processing:", callbackError);
    return null; // Don't kill the stream for callback errors
  }
};

const _processSSEEventInner = (data, currentMessageId, onToken, onMessage) => {
  if (data.error) {
    if (isQuotaError(data.error)) {
      return createQuotaError(data.error);
    }
    // For other errors, return a generic error
    return new Error(data.error?.errorMessage || "An error occurred");
  }

  // Format 2: { type: 'error', errorCode, errorMessage, errorDetail, message_id }
  if (data.type === "error") {
    if (isQuotaError(data)) {
      return createQuotaError(data);
    }
    // For other errors, return a generic error with all available details
    const error = new Error(data.errorMessage || "An error occurred");
    error.errorCode = data.errorCode;
    error.errorDetail = data.errorDetail;
    error.messageId = data.message_id;
    return error;
  }

  // Handle connection lifecycle events
  if (data.status === "connected" || data.status === "initialising") {
    onMessage?.({ type: "connection", status: data.status });
    return null;
  }

  // Handle chat creation
  if (data.chat_created) {
    onMessage?.({
      type: "chat_created",
      chatid: data.chatid,
      message_id: data.message_id,
    });
    return null;
  }

  // Handle token events (text streaming)
  if (data.type === "token" && data.chunk) {
    onToken?.(data.chunk, data.message_id || currentMessageId);
    return null;
  }

  // Handle message events (status/metadata updates)
  if (data.type === "message") {
    onMessage?.({
      type: "message",
      message_id: data.message_id,
      status: data.status,
      metadata: data.metadata,
      callbackMetadata: data.callbackMetadata,
      text: data.text,
      chatid: data.chatid,
      files: data.files,
      images: data.images,
      audios: data.audios,
      videos: data.videos,
    });
    return null;
  }

  // Handle message_sealed events (interleaved reasoning message splitting)
  if (data.type === "message_sealed") {
    onMessage?.({
      type: "message_sealed",
      message_id: data.message_id,
      status: data.status,
      continuation_message_id: data.continuation_message_id,
    });
    return null;
  }

  // Handle plan update events (planner mode)
  if (data.type === "plan_update") {
    onMessage?.({
      type: "plan_update",
      planning_status: data.planning_status,
      active_step: data.active_step,
      total_steps: data.total_steps,
      plan: data.plan,
    });
    return null;
  }

  // Handle step execution events (planner mode)
  if (data.type === "step_execution") {
    onMessage?.({
      type: "step_execution",
      status: data.status,
      step_id: data.step_id,
      agent_id: data.agent_id,
      artifact: data.artifact,
      error: data.error,
    });
    return null;
  }

  // Handle canvas_required event — agent requests canvas creation
  if (data.type === "canvas_required") {
    onMessage?.({
      type: "canvas_required",
      request_id: data.request_id,
      message_id: data.message_id,
      chat_id: data.chat_id,
    });
    return null;
  }

  // Forward any other events (including post_task_completed, post_task_generating_artifacts, etc.)
  onMessage?.(data);
  return null;
};

export default {
  streamAgentResponse,
  QUOTA_ERROR_TYPES,
};
